import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_LLM_MODEL } from '@/constants/models';
import { DEFAULT_IMAGE_MODEL } from '@/constants/imageModels';

export type GenerationMode = 'suggestion' | 'supervised';

interface ModelStore {
  // Selected model for crew runs
  selectedModel: string | null;

  // Dedicated text-to-image model selection
  selectedImageModel: string | null;

  // Generation mode: standard (suggestion) or supervised (section-by-section)
  generationMode: GenerationMode;

  // Model preferences per agent (optional override)
  agentModels: Record<string, string>;

  // Actions
  setSelectedModel: (modelId: string) => void;
  setSelectedImageModel: (modelId: string) => void;
  setGenerationMode: (mode: GenerationMode) => void;
  setAgentModel: (agentId: string, modelId: string) => void;
  removeAgentModel: (agentId: string) => void;
  clearAgentModels: () => void;
}

export const useModelStore = create<ModelStore>()(
  persist(
    (set) => ({
      selectedModel: DEFAULT_LLM_MODEL,
      selectedImageModel: DEFAULT_IMAGE_MODEL,
      generationMode: 'suggestion' as GenerationMode,
      agentModels: {},

      setSelectedModel: (modelId) => set({ selectedModel: modelId }),
      setSelectedImageModel: (modelId) => set({ selectedImageModel: modelId }),
      setGenerationMode: (mode) => set({ generationMode: mode }),

      setAgentModel: (agentId, modelId) =>
        set((state) => ({
          agentModels: { ...state.agentModels, [agentId]: modelId },
        })),

      removeAgentModel: (agentId) =>
        set((state) => {
          const { [agentId]: _, ...rest } = state.agentModels;
          return { agentModels: rest };
        }),

      clearAgentModels: () => set({ agentModels: {} }),
    }),
    {
      name: 'model-storage',
    }
  )
);
